import { useState } from 'react';
import { getPose } from '../data/poses';
import { formatDuration, totalSeconds, type RoutinesApi } from '../hooks/useRoutines';
import { navigate } from '../router';
import { primeSpeech } from '../audio/announcer';
import { primeAudio } from '../audio/clipAnnouncer';
import { useAuth } from '../auth/AuthContext';

// Unlock both cue engines from the Play tap (browsers require a gesture).
const primeCues = () => {
  primeSpeech();
  primeAudio();
};

const shareLink = (id: string) => `${window.location.origin}/#/r/${id}`;

interface Props {
  routines: RoutinesApi;
}

export function RoutinesListPage({ routines }: Props) {
  const list = routines.routines;
  const { status } = useAuth();
  const signedIn = status === 'authenticated';
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Routines</h1>
        <div className="head-actions">
          {signedIn && (
            <a className="btn" href="#/routines/generate">
              ✨ Generate
            </a>
          )}
          <a className="btn primary" href="#/routines/new">
            + New routine
          </a>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="empty">
          <p>No routines yet.</p>
          <a className="btn primary" href="#/routines/new">
            Build your first routine
          </a>
        </div>
      ) : (
        <div className="routine-list">
          {list.map((r) => {
            const names = r.steps
              .map((s) => getPose(s.poseId)?.nameEnglish)
              .filter(Boolean)
              .join(' · ');
            return (
              <div className="routine-row" key={r.id}>
                <div className="routine-meta">
                  <h2>
                    {r.name}
                    {r.builtin && <span className="shared-badge default">Default</span>}
                    {r.public && <span className="shared-badge">Shared</span>}
                  </h2>
                  <div className="routine-sub">
                    {r.steps.length} pose{r.steps.length === 1 ? '' : 's'} ·{' '}
                    {formatDuration(totalSeconds(r))} total
                  </div>
                  {names && <div className="routine-poses">{names}</div>}
                </div>
                <div className="routine-actions">
                  <a className="btn primary" href={`#/routines/${r.id}/play`} onClick={primeCues}>
                    ▶ Play
                  </a>
                  <a className="btn" href={`#/routines/${r.id}/edit`}>
                    {r.builtin ? 'Customize' : 'Edit'}
                  </a>
                  {/* Built-in starter routines are read-only: no Share, no Delete. */}
                  {!r.builtin && signedIn && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        if (!r.public) routines.setPublic(r.id, true);
                        setCopied(false);
                        setSharingId(sharingId === r.id ? null : r.id);
                      }}
                    >
                      {r.public ? 'Sharing ✓' : 'Share'}
                    </button>
                  )}
                  {!r.builtin && (
                    <button
                      type="button"
                      className="btn danger"
                      onClick={() => {
                        if (confirm(`Delete "${r.name}"?`)) routines.remove(r.id);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>

                {sharingId === r.id && (
                  <div className="share-panel">
                    <input
                      className="share-link"
                      readOnly
                      value={shareLink(r.id)}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => {
                        navigator.clipboard
                          ?.writeText(shareLink(r.id))
                          .then(() => setCopied(true))
                          .catch(() => {});
                      }}
                    >
                      {copied ? 'Copied ✓' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      className="btn sm danger"
                      onClick={() => {
                        routines.setPublic(r.id, false);
                        setSharingId(null);
                      }}
                    >
                      Stop sharing
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="hint">
        Routines are saved on this device{signedIn ? ' and your account' : ''}.{' '}
        <a href="#/" onClick={() => navigate('/')}>
          Browse poses
        </a>{' '}
        to add more.
      </p>
    </>
  );
}
